import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { fetchCertificationProgress, updateCertificationStatus, fetchCertificationJourney } from '@/api/certifications'
import { Check, Circle, Clock, Award, ChevronRight } from 'lucide-react'
import { cn, getLevelColor, formatDate } from '@/lib/utils'
import { toast } from 'sonner'

const roleTypes = [
  { value: 'sales_person', label: 'Sales Person' },
  { value: 'pre_sales', label: 'Pre-Sales' },
  { value: 'post_sales', label: 'Post-Sales' },
  { value: 'technical', label: 'Technical' },
]

export function CertificationJourney() {
  const [selectedRole, setSelectedRole] = useState('sales_person')
  
  const { data: progressData, isLoading: isLoadingProgress } = useQuery({
    queryKey: ['certification-progress'],
    queryFn: fetchCertificationProgress,
  })
  
  const { data: journeyData, isLoading: isLoadingJourney } = useQuery({
    queryKey: ['certification-journey', selectedRole],
    queryFn: () => fetchCertificationJourney(selectedRole),
  })

  if (isLoadingProgress) {
    return <div className="h-96 animate-pulse rounded-lg bg-muted" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Certification Journey</h1>
          <p className="text-muted-foreground">
            Track your Cloudflare partner certification progress
          </p>
        </div>
        <div className="w-full sm:w-64">
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger>
              <SelectValue placeholder="Select your role" />
            </SelectTrigger>
            <SelectContent>
              {roleTypes.map((role) => (
                <SelectItem key={role.value} value={role.value}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Overall Progress */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-cloudflare-orange/10">
              <Award className="h-8 w-8 text-cloudflare-orange" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Overall Progress</h3>
              <div className="mt-2 flex items-center gap-4">
                <Progress value={progressData?.overallProgress || 0} className="flex-1" />
                <span className="text-sm font-medium">
                  {progressData?.overallProgress || 0}%
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {progressData?.completedItems || 0} of {progressData?.totalItems || 0} requirements completed
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Level Progress Tabs */}
      <Tabs defaultValue="Fundamentals" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          {['Fundamentals', 'Essentials', 'Advanced'].map((level) => (
            <TabsTrigger key={level} value={level}>
              {level}
              <Badge variant="secondary" className="ml-2">
                {journeyData?.levelStatus?.[level]?.percentage || 0}%
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>

        {['Fundamentals', 'Essentials', 'Advanced'].map((level) => (
          <TabsContent key={level} value={level}>
            <CertificationLevel 
              level={level} 
              items={journeyData?.journey?.[level] || []}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}

function CertificationLevel({ 
  level, 
  items 
}: { 
  level: string
  items: any[]
}) {
  const queryClient = useQueryClient()
  
  const mutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => 
      updateCertificationStatus(id, status as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certification-progress'] })
      queryClient.invalidateQueries({ queryKey: ['certification-journey'] })
      toast.success('Status updated successfully')
    },
    onError: () => {
      toast.error('Failed to update status')
    }
  })

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">No certifications available for this role and level.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className={cn('h-3 w-3 rounded-full', getLevelColor(level))} />
          {level} Level
        </CardTitle>
        <CardDescription>
          Complete these requirements to advance your certification
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {items.map((item, index) => (
            <div 
              key={item.id} 
              className={cn(
                'flex items-start gap-4 rounded-lg border p-4 transition-colors',
                item.status === 'completed' ? 'bg-muted/50' : 'hover:bg-muted/30'
              )}
            >
              {/* Status Icon */}
              <div className="mt-0.5">
                {item.status === 'completed' ? (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500">
                    <Check className="h-5 w-5 text-white" />
                  </div>
                ) : item.status === 'in_progress' ? (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-500">
                    <Clock className="h-5 w-5 text-white" />
                  </div>
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300">
                    <Circle className="h-4 w-4 text-gray-300" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className={cn(
                    'font-medium',
                    item.status === 'completed' && 'line-through text-muted-foreground'
                  )}>
                    {item.title}
                  </h3>
                  <Badge variant="secondary">{item.type}</Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {item.description}
                </p>
                
                {/* Status Actions */}
                <div className="mt-3 flex items-center gap-2">
                  {item.status !== 'completed' && (
                    <Button 
                      size="sm" 
                      onClick={() => mutation.mutate({ id: item.id, status: 'completed' })}
                      disabled={mutation.isPending}
                    >
                      Mark Complete
                    </Button>
                  )}
                  {item.status === 'not_started' && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => mutation.mutate({ id: item.id, status: 'in_progress' })}
                      disabled={mutation.isPending}
                    >
                      Start
                    </Button>
                  )}
                  {item.status !== 'not_started' && (
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => mutation.mutate({ id: item.id, status: 'not_started' })}
                      disabled={mutation.isPending}
                    >
                      Reset
                    </Button>
                  )}
                </div>
              </div>

              {/* Order number */}
              <div className="text-sm text-muted-foreground">
                {String(index + 1).padStart(2, '0')}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
